// The territory map: New York's five boroughs drawn from their real outlines (simplified),
// with New Jersey, Westchester and Nassau around them, styled for the game's night-time look.
import type { ReactNode } from 'react';
import { MAP_W, MAP_H, BOROUGH_GEO, LAND_GEO, PARK_PATH, LANDMARKS, BRIDGES } from './nycGeo.ts';

/** Where each borough's name sits (tuned by eye from the centroids). */
export const BOROUGH_LABEL: Record<string, { x: number; y: number; rotate?: number }> = {
  manhattan: { x: 312, y: 168, rotate: -62 },
  bronx: { x: 420, y: 92 },
  queens: { x: 488, y: 300 },
  brooklyn: { x: 352, y: 392 },
  staten_island: { x: 132, y: 482 },
};
/** Where the armies stand in a battle replay: attackers come in from outside, defenders hold the middle. */
export const BATTLE_SPOTS: Record<string, { atk: [number, number]; def: [number, number] }> = {
  manhattan: { atk: [228, 238], def: [318, 215] },
  bronx: { atk: [352, 158], def: [436, 104] },
  queens: { atk: [400, 210], def: [500, 318] },
  brooklyn: { atk: [262, 330], def: [360, 410] },
  staten_island: { atk: [214, 408], def: [124, 492] },
};
export { MAP_W, MAP_H };

export interface MapBorough {
  id: string;
  name: string;
  fill: string;
  ownerLabel: string;
  shield?: boolean;
  mine?: boolean;
  siege?: boolean;
}

/** v0.8: a bronze statue on a stone plinth (the monthly #1 player). Drawn at 0,0 → 24×40. */
function StatueShape() {
  return (
    <g>
      <rect x="2" y="30" width="20" height="10" fill="#8a8678" stroke="#222" strokeWidth="0.8" />
      <rect x="5" y="27" width="14" height="4" fill="#a8a496" stroke="#222" strokeWidth="0.8" />
      <path d="M8 27 l1 -10 h6 l1 10z" fill="#9a6a2a" stroke="#3a2200" strokeWidth="0.7" />
      <path d="M7 18 q5 -4 10 0 l-1 -4 h-8z" fill="#9a6a2a" stroke="#3a2200" strokeWidth="0.7" />
      <circle cx="12" cy="10" r="3.2" fill="#b07a32" stroke="#3a2200" strokeWidth="0.7" />
      <path d="M7 8 h10 l-1.5 -2 h-7z M9 6 q3 -4 6 0z" fill="#6a4418" stroke="#3a2200" strokeWidth="0.6" />
      <path d="M16 17 l5 -6" stroke="#9a6a2a" strokeWidth="2" strokeLinecap="round" />
      <path d="M10 15 q2 2 4 0" stroke="#e0b060" strokeWidth="0.6" fill="none" opacity="0.8" />
    </g>
  );
}

/** The statue as a small standalone icon (profiles, lists). */
export function StatueIcon({ size = 20 }: { size?: number }) {
  return <svg width={size * 0.6} height={size} viewBox="0 0 24 40" aria-hidden="true" style={{ verticalAlign: 'middle' }}><StatueShape /></svg>;
}

export function NycMap({ boroughs, selected, onSelect, size = 600, attacking, captured, statue, children }: {
  boroughs: MapBorough[]; selected: string | null; onSelect: (id: string) => void; size?: number; attacking?: string | null; captured?: string | null;
  statue?: { name: string; monthName: string } | null; children?: ReactNode;
}) {
  return (
    <svg className="nyc-map" viewBox={`0 0 ${MAP_W} ${MAP_H}`} width={size} height={(size * MAP_H) / MAP_W} role="img" aria-label="Map of New York's five boroughs">
      <defs>
        <pattern id="waves" width="16" height="9" patternUnits="userSpaceOnUse">
          <path d="M0 5 q4 -3.5 8 0 t8 0" stroke="#24476e" strokeWidth="0.8" fill="none" />
        </pattern>
        <pattern id="landHatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <path d="M0 0 V7" stroke="#3a3a3a" strokeWidth="1" />
        </pattern>
        <radialGradient id="seaGlow" cx="0.45" cy="0.45" r="0.75">
          <stop offset="0" stopColor="#123050" />
          <stop offset="1" stopColor="#081626" />
        </radialGradient>
        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="4" /></filter>
        <filter id="boroughShadow" x="-10%" y="-10%" width="120%" height="120%"><feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.8" /></filter>
      </defs>
      <rect width={MAP_W} height={MAP_H} fill="url(#seaGlow)" />
      <rect width={MAP_W} height={MAP_H} fill="url(#waves)" opacity="0.7" />
      {/* The neighbours (not playable) */}
      {Object.entries(LAND_GEO).map(([k, d]) => (
        <g key={k}>
          <path d={d} fill="#1c1c1c" stroke="#4a4030" strokeWidth="1" />
          <path d={d} fill="url(#landHatch)" opacity="0.8" />
        </g>
      ))}
      <text x="70" y="150" className="map-region">NEW JERSEY</text>
      <text x="470" y="22" className="map-region">WESTCHESTER</text>
      <text x="560" y="330" className="map-region" transform="rotate(-80 560 330)">LONG ISLAND</text>
      <text x="262" y="236" className="map-water" transform="rotate(-62 262 236)">Hudson River</text>
      <text x="262" y="430" className="map-water">Upper Bay</text>
      <text x="300" y="560" className="map-water">Atlantic Ocean</text>
      {boroughs.map((b) => {
        const g = BOROUGH_GEO[b.id];
        const L = BOROUGH_LABEL[b.id];
        if (!g || !L) return null;
        const on = selected === b.id;
        return (
          <g key={captured === b.id ? `${b.id}-cap` : b.id} className={`borough ${attacking === b.id ? 'attacking' : ''} ${captured === b.id ? 'captured' : ''} ${b.siege ? 'besieged' : ''}`}
            onClick={() => onSelect(b.id)} style={{ cursor: 'pointer' }} role="button" aria-label={`${b.name}: ${b.ownerLabel}`}>
            {on && <path d={g.path} fill="#ffcc33" filter="url(#glow)" opacity="0.6" />}
            <path className="bpath" d={g.path} fill={b.fill} stroke={on ? '#ffe9a8' : b.mine ? '#ffcc33' : '#0a0a0a'} strokeWidth={on ? 2.5 : b.mine ? 1.8 : 1.2} strokeLinejoin="round" filter="url(#boroughShadow)" />
            {b.id === 'manhattan' && <path d={PARK_PATH} fill="#2a4a22" stroke="#122a10" strokeWidth="0.6" pointerEvents="none" />}
            <g transform={L.rotate ? `rotate(${L.rotate} ${L.x} ${L.y})` : undefined} pointerEvents="none">
              <text x={L.x} y={L.y} className="map-name" fontSize={b.id === 'manhattan' ? 15 : 19}>{b.name}</text>
              <text x={L.x} y={L.y + (b.id === 'manhattan' ? 13 : 16)} className={`map-owner ${b.mine ? 'mine' : ''}`}>{b.ownerLabel}{b.shield ? ' (protected)' : ''}</text>
            </g>
            {b.siege && (
              <g className="siege-mark" pointerEvents="none" transform={`translate(${L.x + (b.id === 'manhattan' ? 34 : 0)} ${L.y - (b.id === 'manhattan' ? 34 : 32)})`}>
                <circle r="10" fill="#900" stroke="#ffcc33" strokeWidth="1.5" />
                <path d="M-5 -5 L5 5 M5 -5 L-5 5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
              </g>
            )}
          </g>
        );
      })}
      {/* Bridges and landmarks */}
      {Object.entries(BRIDGES).map(([k, [a, b]]) => <path key={k} d={`M${a[0]} ${a[1]} L${b[0]} ${b[1]}`} className="map-bridge" pointerEvents="none"><title>{k}</title></path>)}
      {Object.entries(LANDMARKS).filter(([k]) => k !== 'Central Park' && k !== 'Brooklyn Bridge').map(([k, [x, y]]) => (
        <g key={k} pointerEvents="none" className="map-landmark">
          <circle cx={x} cy={y} r="2.6" />
          <text x={x + 5} y={y + 3}>{k}</text>
        </g>
      ))}
      {/* compass rose and title */}
      <g transform="translate(548 520)" className="map-compass" pointerEvents="none">
        <circle r="22" />
        <path d="M0 -30 L5 0 L0 30 L-5 0 Z" className="needle" />
        <path d="M-30 0 L0 4 L30 0 L0 -4 Z" />
        <text y="-34" textAnchor="middle">N</text>
      </g>
      <g transform="translate(96 560)" pointerEvents="none">
        <rect x="-82" y="-16" width="164" height="26" rx="3" className="map-cartouche" />
        <text textAnchor="middle" y="2" className="map-title">NEW YORK CITY</text>
      </g>
      {statue && (
        <g className="map-statue" pointerEvents="none">
          <title>{`Statue of ${statue.name}, #1 for ${statue.monthName}`}</title>
          <ellipse cx={LANDMARKS['Central Park'][0] + 4} cy={LANDMARKS['Central Park'][1] + 26} rx="14" ry="5" fill="#ffcc33" opacity="0.25" />
          <g transform={`translate(${LANDMARKS['Central Park'][0] - 8} ${LANDMARKS['Central Park'][1] - 14})`}><StatueShape /></g>
          <text x={LANDMARKS['Central Park'][0] - 12} y={LANDMARKS['Central Park'][1] + 2} textAnchor="end" className="map-statue-name">{statue.name}</text>
          <text x={LANDMARKS['Central Park'][0] - 12} y={LANDMARKS['Central Park'][1] + 12} textAnchor="end" className="map-statue-sub">statue · {statue.monthName}</text>
        </g>
      )}
      {children}
    </svg>
  );
}

/** Small original icons for territory units. */
/** The art for one unit type, in a 64×48 box (used by icons and by battle replays). */
export function unitArt(unit: string): ReactNode {
  const art: Record<string, ReactNode> = {
    street_crew: <g><circle cx="20" cy="18" r="6" fill="#c9a07a" /><path d="M10 40 q10 -16 20 0z" fill="#3a5a8a" /><path d="M30 14 l8 -10" stroke="#a87a3a" strokeWidth="3" /><circle cx="44" cy="20" r="5" fill="#c9a07a" /><path d="M36 40 q8 -14 16 0z" fill="#4a4a4a" /></g>,
    shooters: <g><circle cx="24" cy="16" r="7" fill="#d8b08a" /><path d="M12 42 q12 -20 24 0z" fill="#222" /><path d="M30 26 h20 v4 h-20z" fill="#888" /><path d="M16 9 h16 l-2 -5 h-12z" fill="#111" /></g>,
    bodyguards: <g><circle cx="32" cy="14" r="8" fill="#b88a6a" /><path d="M14 46 q18 -28 36 0z" fill="#111" /><path d="M26 12 h12 v3 h-12z" fill="#000" /><path d="M10 24 l8 -2 v20 l-8 -2z" fill="#5a6a7a" /></g>,
    enforcer_squad: <g>{[12, 30, 48].map((x) => <g key={x}><circle cx={x} cy="16" r="6" fill="#c9a07a" /><path d={`M${x - 9} 42 q9 -18 18 0z`} fill="#3a2a2a" /></g>)}</g>,
    armored_car: <g><rect x="6" y="20" width="52" height="20" rx="3" fill="#4a5a4a" /><rect x="14" y="12" width="26" height="12" rx="2" fill="#5a6a5a" /><rect x="18" y="15" width="8" height="5" fill="#9cf" /><circle cx="18" cy="42" r="5" fill="#111" /><circle cx="46" cy="42" r="5" fill="#111" /></g>,
    hit_squad: <g>{[16, 40].map((x) => <g key={x}><circle cx={x} cy="16" r="7" fill="#d8b08a" /><path d={`M${x - 11} 44 q11 -22 22 0z`} fill="#000" /><path d={`M${x - 8} 9 h16 l-2 -5 h-12z`} fill="#222" /></g>)}<path d="M8 30 h48" stroke="#c22" strokeWidth="2" strokeDasharray="3 2" /></g>,
    // the local gangs (only in replays)
    gang: <g>{[18, 44].map((x) => <g key={x}><circle cx={x} cy="17" r="6.5" fill="#b8906a" /><path d={`M${x - 7} 14 h14 v3 h-14z`} fill="#8a1a10" /><path d={`M${x - 10} 44 q10 -22 20 0z`} fill="#4a3a2a" /></g>)}<path d="M28 30 l8 -12" stroke="#777" strokeWidth="2.5" /></g>,
  };
  return art[unit] ?? art.street_crew;
}

/** Small original icons for territory units. */
export function UnitIcon({ unit, size = 36 }: { unit: string; size?: number }) {
  return (
    <svg className="icon item-icon" width={size} height={size} viewBox="0 0 64 48" aria-hidden="true">
      <rect width="64" height="48" fill="#1a1a22" />
      {unitArt(unit)}
    </svg>
  );
}
